/**
 * The pixel to draw the page's own character on along one axis, kept steady (ADR-032's note of 2026-10-10): the
 * pixel last drawn stands in for a new one a single pixel against the rendered motion, and, while the rendered
 * position does not move, for a new one a single pixel away as long as it stays within a pixel and a half of the
 * rendered position. Any other pixel is taken as it comes: one with the motion, the same one, a move of more than a
 * pixel (a correction's or a respawn's) and the first.
 *
 * @example
 * steadyAxis(4609, 4610, 0.26, 4609.13);
 * // 4610: the end of a diagonal walk, whose y on x's parity was a pixel ahead of its rounding, does not step back
 *
 * @param candidate The pixel the drawing rule gives this frame.
 * @param last The pixel last drawn, NaN before the first.
 * @param moved How far the rendered position moved along the axis since the last frame, in world pixels.
 * @param rendered The rendered position along the axis this frame, in world pixels.
 * @returns The pixel to draw.
 */
export function steadyAxis(candidate: number, last: number, moved: number, rendered: number): number {
  const step = candidate - last;
  if (Number.isNaN(last) || step === 0 || Math.abs(step) > 1) {
    return candidate;
  }
  if (moved !== 0) {
    return Math.sign(step) === -Math.sign(moved) ? last : candidate;
  }
  return Math.abs(last - rendered) <= 1.5 ? last : candidate;
}
